import { createPortal } from 'react-dom';
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector } from 'react-redux';
 
const CustomModal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  onSubmit,
  showButton,
  showSubmitButton = true,
  showCancelButton = true,
  fontSize = "16px",
  fontWeight = "400",
  subtitleFontSize = "14px",
  subtitleFontWeight = "400",
  children,
  modalSize = "",
  minHeight = "200px",
  maxHeight = "550px",
  isCentered = false,
  submitButtonText = "Save", // New prop with default value "Save"
  submitButtonDisabled = false, // New prop for disabling submit button
  /** Merged into submit button style after base (e.g. { width: "auto", minWidth: "160px", whiteSpace: "nowrap" }). */
  submitButtonStyle = null,
  /** Renders next to the close control (e.g. “New …” shortcut link) */
  headerActions = null,
  /** Base z-index for overlay; use a higher value when stacking modals (e.g. crop over library). */
  zIndexBase = 9999,
  /** Tighter title/subtitle/header and body padding (e.g. media library). */
  compactHeader = false,
  /** Override compact header padding (CSS shorthand). */
  compactHeaderPadding = null,
  /** When compactHeader: muted one-line note beside title (saves a body row). */
  compactHeaderNote = null,
  /** Optional max-width for the modal dialog (e.g. "400px"). */
  dialogMaxWidth = null,
  /** modal-body overflow (e.g. "hidden" when children manage their own scroll). */
  bodyOverflow = "auto",
  /** When true, clicking the dimmed backdrop closes the modal. Default false. */
  closeOnOutsideClick = false,

}) => {
  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  
  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();

  // Get theme color from centralized hook
  const themeColor = useThemeColor();

  
  if (!isOpen) return null;

  const dialogClassName = [
    "modal-dialog",
    modalSize,
    isCentered ? "modal-dialog-centered" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const isFitContentDialog = dialogMaxWidth === "fit-content";

  const handleBackdropClick = (e) => {
    if (!closeOnOutsideClick) return;
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const modalMarkup = (
    <div
      className={`modal fade show${isCentered ? "" : " d-block"}`}
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        zIndex: zIndexBase,
        backdropFilter: "blur(3px)",
        WebkitBackdropFilter: "blur(3px)",
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100%",
        overflow: "auto",
        display: isCentered ? "flex" : "block",
        alignItems: isCentered ? "center" : undefined,
        justifyContent: isCentered ? "center" : undefined,
        padding: isCentered ? "1.5rem" : 0,
      }}
      onClick={handleBackdropClick}
    >
      <div
        className={dialogClassName}
        style={{
          position: "relative",
          zIndex: zIndexBase + 1,
          margin: isCentered ? 0 : "0.5rem auto",
          marginLeft: isCentered ? undefined : "auto",
          marginRight: isCentered ? undefined : "auto",
          pointerEvents: "auto",
          flexShrink: 0,
          width: isCentered ? (isFitContentDialog ? "fit-content" : "100%") : undefined,
          maxWidth: isCentered
            ? (isFitContentDialog ? "calc(100vw - 3rem)" : dialogMaxWidth || "min(900px, calc(100vw - 3rem))")
            : dialogMaxWidth || undefined,
          ...(dialogMaxWidth && !isCentered
            ? { width: "calc(100% - 2rem)" }
            : {}),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="modal-content"
          style={{
            borderRadius: "20px",
            minHeight: minHeight,
            maxHeight: maxHeight,
            width: isFitContentDialog ? "fit-content" : "100%",
            maxWidth: "100%",
            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
            border: isDarkMode ? '1px solid #404040' : '1px solid #dee2e6',
            color: isDarkMode ? '#ffffff' : '#000000',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            zIndex: zIndexBase + 2,
            // Ensure inner scroll areas and content don’t create a second box
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            className={`modal-header d-flex ${compactHeader ? "align-items-center" : "align-items-start"}`}
            style={{ 
              border: "none",
              flexShrink: 0,
              ...(compactHeader
                ? { padding: compactHeaderPadding || "4px 12px 3px" }
                : { paddingBottom: "0px" }),
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#000000',
            }}
          >
            <div
              className={`flex-grow-1 me-2 ${compactHeader ? "d-flex align-items-center flex-wrap" : ""}`}
              style={{
                minWidth: 0,
                ...(compactHeader ? { gap: "8px 12px" } : {}),
              }}
            >
              <h5
                className="modal-title"
                style={{ 
                  fontSize: fontSize, 
                  fontWeight: fontWeight,
                  lineHeight: compactHeader ? 1.15 : undefined,
                  margin: compactHeader ? 0 : undefined,
                  color: isDarkMode ? '#ffffff' : '#000000',
                  ...(compactHeader ? { flexShrink: 0 } : {}),
                }}
              >
                {title}
              </h5>
              {compactHeader && compactHeaderNote && (
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 400,
                    lineHeight: 1.25,
                    color: isDarkMode ? "#868e96" : "#6c757d",
                    flex: "1 1 140px",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={compactHeaderNote}
                >
                  {compactHeaderNote}
                </span>
              )}
              {subtitle && !(compactHeader && compactHeaderNote) && (
                <p
                  className="modal-subtitle"
                  style={{
                    fontSize: subtitleFontSize,
                    fontWeight: subtitleFontWeight,
                    margin: compactHeader ? "3px 0 0 0" : "8px 0 0 0",
                    lineHeight: compactHeader ? 1.35 : undefined,
                    color: isDarkMode ? '#adb5bd' : "#666",
                  }}
                >
                  {subtitle}
                </p>
              )}
            </div>
            <div
              className={`d-flex align-items-center gap-2 flex-shrink-0 ${compactHeader ? "" : "pt-1"}`}
              onClick={(e) => e.stopPropagation()}
            >
              {headerActions}
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                style={compactHeader ? { transform: "scale(0.82)", opacity: 0.85 } : undefined}
              ></button>
            </div>
          </div>
          <div
            className="modal-body"
            style={{
              overflowY: bodyOverflow,
              overflowX: "hidden",
              flex: "1 1 auto",
              minHeight: 0,
              maxHeight: `calc(${maxHeight} - ${compactHeader ? "44px" : "100px"})`,
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#000000',
              ...(compactHeader
                ? { padding: "4px 12px 12px" }
                : {}),
            }}
          >
            {children}
          </div>

          {showButton && (
            <div className="modal-footer" style={{ border: "none" }}>
              {showCancelButton && (
                <button
                  onClick={onClose}
                  type="button"
                  className="btn"
                  style={{ ...buttonStyles.base, ...buttonStyles.cancel }}
                >
                  Cancel
                </button>
              )}

              {showSubmitButton && (
                <button
                  onClick={onSubmit}
                  type="button"
                  className="btn"
                  disabled={submitButtonDisabled}
                  style={{ 
                    ...buttonStyles.base,
                    ...(submitButtonStyle && typeof submitButtonStyle === "object" ? submitButtonStyle : {}),
                    backgroundColor: submitButtonDisabled ? "#C4C4C4" : themeColor,
                    background: submitButtonDisabled ? "#C4C4C4" : `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: `1px solid ${themeColor}`,
                    boxShadow: submitButtonDisabled ? "none" : `0 4px 15px ${themeColor}40`,
                    transition: 'all 0.3s ease',
                    ...(submitButtonDisabled ? buttonStyles.disabled : {})
                  }}
                  onMouseEnter={(e) => {
                    if (!submitButtonDisabled) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = `0 6px 20px ${themeColor}60`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitButtonDisabled) {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = `0 4px 15px ${themeColor}40`;
                    }
                  }}
                >
                  {submitButtonText} {/* Use the new prop here */}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalMarkup, document.body);
};

const buttonStyles = {
  base: {
    fontFamily: "Open Sans, sans-serif",
    fontWeight: "600",
    minWidth: "107px",
    width: "auto",
    borderRadius: "10px",
    minHeight: "40px",
    height: "auto",
    padding: "8px 16px",
    border: "none",
    outline: "none",
    fontSize: "14px",
    lineHeight: "1.25",
    whiteSpace: "nowrap",
    cursor: "pointer",
    color: "white",
  },
  cancel: {
    backgroundColor: "#C4C4C4",
    color: "#000",
  },
  disabled: {
    backgroundColor: "#C4C4C4",
    color: "#666",
    cursor: "not-allowed",
    opacity: 0.6,
  },
};

export default CustomModal;